import React, { useState } from 'react';
import { Modal, Input, Form, Select, ConfigProvider, Spin, message } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { Button } from './components/Button';
import styles from './pc.module.scss';
import { useOptions } from './hooks/useOptions';
import { useLanType, useTranslation } from './LangContext';
import { createBrandItem } from './apis';
import { MTDIcon } from '../../components/Icon';

const notFound = (title: string, content: string) => (
  <div className={styles.notFound}>
    <div className={styles.notFoundTitle}>{title}</div>
    <div className={styles.notFoundP}>
      <p dangerouslySetInnerHTML={{ __html: content }} />
    </div>
  </div>
);

const CompanySelect = (props: any) => {
  const { value, lanType, t, onChange, brandName } = props;
  const { options, onSearch, fetching } = useOptions({
    queryApi: 'queryCompanyList',
    queryParams: { lanType, brand: brandName },
    dependencies: [lanType, brandName],
    keywordsKey: 'company',
  });

  return (
    <Select
      value={value}
      placeholder={t('formConfig.contactCompanyPlaceholder', '请输入企业名称查询')}
      options={options}
      showSearch
      notFoundContent={
        fetching ? (
          <Spin size="small" />
        ) : (
          notFound(t('contactCompanyNotFound.title'), t('contactCompanyNotFound.content'))
        )
      }
      onSearch={onSearch}
      allowClear
      popupClassName={styles.popup}
      onChange={onChange}
    />
  );
};

const BrandSelect = (props: any) => {
  const { value, lanType, t, onChange, brandName } = props;
  const { options, onSearch, fetching } = useOptions({
    queryApi: 'queryBrandList',
    queryParams: { lanType, brandName },
    dependencies: [brandName, lanType],
    keywordsKey: 'brand',
  });

  return (
    <Select
      value={value}
      placeholder={t('formConfig.brandPlaceholder', '请输入品牌名称查询')}
      options={options}
      showSearch
      notFoundContent={fetching ? <Spin size="small" /> : notFound(t('notFound.title'), t('notFound.content'))}
      onSearch={onSearch}
      allowClear
      popupClassName={styles.popup}
      onChange={onChange}
    />
  );
};

export const PCModal = () => {
  const [form] = Form.useForm();
  // 输入框打开
  const [visible, setVisible] = useState(false);
  // 按钮是否可以点击
  const [display, setDisplay] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [brandName, setBrandName] = useState('');

  const [messageApi, contextHolder] = message.useMessage({ top: 200 });

  const lanType = useLanType();
  const { t } = useTranslation();

  const onClick = () => {
    setVisible(true);
  };
  const onClose = () => {
    setVisible(false);
    setDisplay(false);
    form.setFieldsValue({
      brandName: undefined,
      contactCompany: undefined,
      contactName: '',
      contactPhone: '',
    });
  };
  const onSubmit = () => {
    form
      .validateFields()
      .then((formValues) => {
        createBrandItem({ ...formValues, businessType: 'LAO_ZI_HAO' })
          .then(() => {
            messageApi.open({
              content: (
                <div className={styles.messageContainer}>
                  <MTDIcon icon="mtdicon-success-o" width="20" height="20" className={styles.successIcon} />
                  {t('successTips')}
                </div>
              ),
            });
            onClose();
          })
          .catch(() => {
            messageApi.open({
              content: (
                <div className={styles.messageContainer}>
                  <MTDIcon icon="mtdicon-error" width="20" height="20" className={styles.successIcon} />
                  {t('failTips')}
                </div>
              ),
            });
          });
      })
      .catch(() => {});
  };
  // 是否展示额外的输入框
  const onValuesChange = (_: Record<string, any>, allValues: Record<string, any>) => {
    const changed = allValues.brandName;
    if (brandName !== changed) {
      setBrandName(changed);
      form.setFieldValue('contactCompany', undefined);
    }
    if (changed) {
      setDisplay(true);
    } else {
      setDisplay(false);
    }
    setSubmitDisabled(
      !(allValues.brandName && allValues.contactName && allValues.contactCompany && allValues.contactPhone),
    );
  };

  return (
    <>
      <ConfigProvider
        locale={zhCN}
        theme={{
          components: {
            Message: { colorBgElevated: 'rgba(0, 0, 0, 0.85)' },
          },
        }}
        autoInsertSpaceInButton={false}
      >
        {contextHolder}
        <div className={styles.modalWrap}>
          <Button className={styles.modalBtn} onClick={onClick}>
            {t('applyBtnTitle')}
          </Button>
          <Modal
            open={visible}
            title={t('applyTitle', '申请美团“中华老字号”支持计划')}
            width={520}
            closable
            className={styles.modal}
            afterClose={onClose}
            okText={t('formConfig.submitBtnText', '提交')}
            closeIcon={<MTDIcon icon="mtdicon-close" width="22" height="22" />}
            cancelButtonProps={{
              className: styles.modalCancelButton,
            }}
            zIndex={10000}
            okButtonProps={{
              disabled: submitDisabled,
              className: styles.modalButton,
            }}
            onCancel={onClose}
            onOk={onSubmit}
          >
            {visible && (
              <>
                {' '}
                <div className="pb-4">
                  <div className={styles.modalAlert}>
                    <MTDIcon className={styles.alertIcon} width="20" height="20" icon="mtdicon-info-circle" />
                    {t('applyTooltips', '支持计划需要中华老字号品牌/集团负责人填写申请。')}
                  </div>
                </div>
                <Form
                  layout="vertical"
                  form={form}
                  onValuesChange={onValuesChange}
                  initialValues={{ brandName: undefined, contactName: '', contactPhone: '' }}
                >
                  <Form.Item
                    label={t('formConfig.brandLabel', '请选择您的品牌')}
                    name="brandName"
                    rules={[
                      {
                        required: true,
                        message: t('formConfig.brandErrorTips'),
                      },
                    ]}
                  >
                    <BrandSelect lanType={lanType} t={t} />
                  </Form.Item>
                  {display && (
                    <>
                      <Form.Item
                        label={t('formConfig.contactCompany', '请选择您的企业名称')}
                        name="contactCompany"
                        rules={[
                          {
                            required: true,
                            message: t('formConfig.brandErrorTips'),
                          },
                        ]}
                      >
                        <CompanySelect lanType={lanType} brandName={brandName} t={t} />
                      </Form.Item>
                      <Form.Item
                        name="contactName"
                        label={t('formConfig.contactNameLabel')}
                        rules={[
                          {
                            required: true,
                            message: t('formConfig.contactNameErrorTips'),
                          },
                          {
                            max: 20,
                            message: t('formConfig.contactNameErrorTips'),
                          },
                          {
                            min: 1,
                            message: t('formConfig.contactNameErrorTips'),
                          },
                        ]}
                      >
                        <Input placeholder={t('formConfig.contactNamePlaceholder')} />
                      </Form.Item>
                      <Form.Item
                        name="contactPhone"
                        label={t('formConfig.contactPhoneLabel')}
                        rules={[
                          {
                            required: true,
                            message: t('formConfig.contactPhoneErrorTips'),
                          },
                          {
                            pattern: /^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/,
                            message: t('formConfig.contactPhoneErrorTips'),
                          },
                        ]}
                      >
                        <Input placeholder={t('formConfig.contactPhonePlaceholder')} maxLength={11} />
                      </Form.Item>
                    </>
                  )}
                </Form>
              </>
            )}
          </Modal>
        </div>
      </ConfigProvider>
    </>
  );
};
